import React from 'react';
import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Link,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiArrowRight } from 'react-icons/fi';
import { Logo } from '../Brand/Logo';

/** Anchor ids must match ClaudeConnectPage section elements */
const CLAUDE_PAGE_SECTION = {
  howToConnect: 'claude-connection-instructions',
  faq: 'claude-faq',
} as const;

export type PublicHeaderNavMode = 'default' | 'claude';

export interface PublicHeaderProps {
  /** When `claude`, "How it works" and "FAQ" scroll to sections on /claude */
  navMode?: PublicHeaderNavMode;
}

/**
 * Public marketing header used on landing pages and promo pages.
 * Features logo, navigation links, and CTA buttons.
 */
export const PublicHeader: React.FC<PublicHeaderProps> = ({ navMode = 'default' }) => {
  const { t } = useTranslation();
  const howItWorksTo =
    navMode === 'claude'
      ? `/claude#${CLAUDE_PAGE_SECTION.howToConnect}`
      : '/how-to-use';
  const faqClaudeTo = `/claude#${CLAUDE_PAGE_SECTION.faq}`;

  return (
    <Box
      as="header"
      position="fixed"
      top={0}
      left={0}
      right={0}
      zIndex="banner"
      bg="rgba(255, 255, 255, 0.8)"
      backdropFilter="blur(12px)"
      borderBottom="1px solid"
      borderColor="gray.100"
      transition="all 0.3s ease"
    >
      <Container maxW="7xl">
        <Flex h="72px" align="center" justify="space-between">
          <Logo size="md" />
          
          {/* Desktop Navigation */}
          <HStack 
            display={{ base: 'none', lg: 'flex' }} 
            gap={1}
            fontWeight="500"
            fontSize="sm"
          >
            <Link 
              href="/#features" 
              px={4} 
              py={2} 
              rounded="md" 
              color="gray.700"
              _hover={{ bg: 'gray.50', color: 'gray.900' }}
              transition="all 0.2s"
            >
              {t('home.nav.features')}
            </Link>
            <Link 
              asChild
              px={4} 
              py={2} 
              rounded="md" 
              color="gray.700"
              _hover={{ bg: 'gray.50', color: 'gray.900' }}
              transition="all 0.2s"
            >
              <RouterLink to={howItWorksTo}>
                {t('home.nav.how_it_works')}
              </RouterLink>
            </Link>
            <Link
              asChild
              px={4}
              py={2}
              rounded="md"
              color="gray.700"
              _hover={{ bg: 'gray.50', color: 'gray.900' }}
              transition="all 0.2s"
            >
              <RouterLink to="/scenarios">
                Case studies
              </RouterLink>
            </Link>
            <Link
              asChild
              px={4}
              py={2}
              rounded="md"
              color="gray.700"
              _hover={{ bg: 'gray.50', color: 'gray.900' }}
              transition="all 0.2s"
            >
              <RouterLink to="/goals-partners">
                Goals Partners
              </RouterLink>
            </Link>
            <Link
              href="/#mobile"
              px={4}
              py={2}
              rounded="md"
              color="gray.700"
              _hover={{ bg: 'gray.50', color: 'gray.900' }}
              transition="all 0.2s"
            >
              {t('home.nav.mobile')}
            </Link>
            <Link 
              href="/#pricing" 
              px={4} 
              py={2} 
              rounded="md" 
              color="gray.700"
              _hover={{ bg: 'gray.50', color: 'gray.900' }}
              transition="all 0.2s"
            >
              {t('home.nav.pricing')}
            </Link>
            {navMode === 'claude' ? (
              <Link
                asChild
                px={4}
                py={2}
                rounded="md"
                color="gray.700"
                _hover={{ bg: 'gray.50', color: 'gray.900' }}
                transition="all 0.2s"
              >
                <RouterLink to={faqClaudeTo}>{t('home.nav.faq')}</RouterLink>
              </Link>
            ) : (
              <Link
                href="/#faq"
                px={4}
                py={2}
                rounded="md"
                color="gray.700"
                _hover={{ bg: 'gray.50', color: 'gray.900' }}
                transition="all 0.2s"
              >
                {t('home.nav.faq')}
              </Link>
            )}
            <Link 
              asChild
              px={4} 
              py={2} 
              rounded="md" 
              color="gray.700"
              _hover={{ bg: 'gray.50', color: 'gray.900' }}
              transition="all 0.2s"
            >
              <RouterLink to="/dna-kit">
                {t('home.nav.dna_kit')}
              </RouterLink>
            </Link>
          </HStack>

          {/* CTA Buttons */}
          <HStack gap={3}>
            <Button 
              asChild 
              variant="ghost" 
              size="sm"
              fontWeight="500"
              display={{ base: 'none', sm: 'inline-flex' }}
            >
              <RouterLink to="/login">{t('home.nav.login')}</RouterLink>
            </Button>
            <Button 
              asChild 
              variant="solid" 
              colorScheme="green"
              color="white"
              size="sm"
              fontWeight="600"
            >
              <RouterLink to="/register">
                {t('home.nav.get_started')}
                <FiArrowRight size="14px" style={{ marginLeft: '4px' }} />
              </RouterLink>
            </Button>
          </HStack>
        </Flex>
      </Container>
    </Box>
  );
};

export default PublicHeader;

